// Дроп с врагов: что падает и что это делает с дефицитом патронов.
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ ЕЩЁ ДЕСЯТЬ СТРОК В stats.ts. У дропа
// своя главная величина, и она не метрика прогона, а РАЗНИЦА между двумя
// ответами на один вопрос: «дефицит соблюдён» без патронов с трупов и с
// ними. Положить её в METRICS значило бы показывать её на «Обзоре» и
// «Распределениях» рядом с числом комнат — а там она бессмысленна: на
// строках схемы 1 блока drops нет вовсе, и колонка молча заполнилась бы
// нулями. Ноль патронов с трупов и отсутствие механики дропа выглядят
// одинаково; разница между ними — весь смысл этой страницы.
//
// Правило раздела 5 контракта («производные величины не голосуют») здесь
// тоже работает: `ammoWithDrops` — сумма двух других полей, и в детекторе
// выбросов ему делать нечего. Он живёт только в этой сводке.

import type { DropsConfig, RunRow } from './types';
import { describe, type Percentiles } from './stats';

/** Признак прогона, ради которого шанс дропа и подбирают (контракт, §1). */
export const DROPS_BREAK_DEFICIT = 'drops_break_deficit';

/**
 * Есть ли в строке блок дропа. Проверяется по наличию поля, а не по
 * `schemaVersion`: строку схемы 2 без дропа сервер имеет право отдать,
 * и падать на ней нельзя.
 */
export function hasDropData(row: RunRow): boolean {
  return row.drops !== undefined && row.economy.ammoMarginWithDrops !== undefined;
}

export function countDropRows(rows: readonly RunRow[]): number {
  let n = 0;
  for (const row of rows) if (hasDropData(row)) n++;
  return n;
}

export interface DropKindTotals {
  kind: string;
  label: string;
  /** Штук за всю выборку. */
  amount: number;
  /** Штук на прогон. */
  perRun: number;
}

export interface DropSummary {
  /** Строк в выборке всего. */
  rows: number;
  /** Из них со схемой 2 и блоком дропа. Ноль — выборка про дроп молчит. */
  dropRows: number;
  /** Доля прогонов, где дефицит держится БЕЗ патронов с трупов. */
  deficitHeld: number;
  /** Та же доля, посчитанная С патронами с трупов. */
  deficitHeldWithDrops: number;
  /**
   * Доля прогонов, где без дропа игрок был беднее, чем ему хочется,
   * а с дропом стал богаче. Это и есть цена шанса дропа.
   */
  brokenByDrops: number;
  /** Сколько врагов уронили хоть что-то, на прогон. */
  drops: Percentiles;
  ammoFromDrops: Percentiles;
  ammoMargin: Percentiles;
  ammoMarginWithDrops: Percentiles;
  byKind: DropKindTotals[];
  /**
   * Доля убитых врагов, с которых что-то упало. Не то же самое, что шанс
   * дропа в настройках: враг мог не попасть в выборку, а таблица —
   * вернуть «пусто». Показывать как наблюдение, а не как настройку.
   */
  observedDropShare: number;
}

const KIND_LABELS: Record<string, string> = {
  ammo: 'Патроны',
  medkits: 'Аптечки',
  keys: 'Ключи',
  batteries: 'Батареи',
  talons: 'Талоны',
};

const EMPTY: Percentiles = {
  count: 0, min: NaN, p1: NaN, p10: NaN, p25: NaN, p50: NaN,
  p75: NaN, p90: NaN, p99: NaN, max: NaN, mean: NaN, mad: NaN,
};

export function emptyDropSummary(rows = 0): DropSummary {
  return {
    rows,
    dropRows: 0,
    deficitHeld: NaN,
    deficitHeldWithDrops: NaN,
    brokenByDrops: NaN,
    drops: EMPTY,
    ammoFromDrops: EMPTY,
    ammoMargin: EMPTY,
    ammoMarginWithDrops: EMPTY,
    byKind: [],
    observedDropShare: NaN,
  };
}

/**
 * Сводка дропа по выборке.
 *
 * Считается ТОЛЬКО по строкам, у которых блок дропа есть. Смешать их со
 * строками схемы 1 значило бы поделить на число прогонов, часть которых
 * про дроп ничего не знает, и занизить каждую долю ровно на эту часть.
 */
export function summarizeDrops(rows: readonly RunRow[]): DropSummary {
  const withDrops = rows.filter(hasDropData);
  if (withDrops.length === 0) return emptyDropSummary(rows.length);

  let held = 0;
  let heldWithDrops = 0;
  let broken = 0;
  let enemies = 0;
  let dropped = 0;
  const kinds = new Map<string, number>();

  for (const row of withDrops) {
    if (row.economy.respectsAmmoDeficit) held++;
    if (row.economy.respectsAmmoDeficitWithDrops === true) heldWithDrops++;
    if (row.flags.includes(DROPS_BREAK_DEFICIT)) broken++;
    enemies += row.population.enemies;
    const drops = row.drops as NonNullable<RunRow['drops']>;
    dropped += drops.count;
    for (const kind of ['ammo', 'medkits', 'keys', 'batteries', 'talons'] as const) {
      kinds.set(kind, (kinds.get(kind) ?? 0) + drops[kind]);
    }
  }

  const n = withDrops.length;
  return {
    rows: rows.length,
    dropRows: n,
    deficitHeld: held / n,
    deficitHeldWithDrops: heldWithDrops / n,
    brokenByDrops: broken / n,
    drops: describe(withDrops.map((r) => (r.drops as NonNullable<RunRow['drops']>).count)),
    ammoFromDrops: describe(withDrops.map((r) => r.economy.ammoFromDrops ?? 0)),
    ammoMargin: describe(withDrops.map((r) => r.economy.ammoMargin)),
    ammoMarginWithDrops: describe(withDrops.map((r) => r.economy.ammoMarginWithDrops ?? 0)),
    byKind: [...kinds.entries()]
      .filter(([, amount]) => amount > 0)
      .map(([kind, amount]) => ({
        kind,
        label: KIND_LABELS[kind] ?? kind,
        amount,
        perRun: amount / n,
      }))
      .sort((a, b) => b.amount - a.amount),
    observedDropShare: enemies > 0 ? dropped / enemies : NaN,
  };
}

// --- таблица дропа ----------------------------------------------------------

export interface DropTableRow {
  kind: string;
  amount: number;
  weight: number;
  /** Доля веса внутри таблицы. */
  share: number;
  /**
   * Вероятность увидеть эту строку с одного трупа: шанс дропа × доля веса.
   * Для строки «пусто» это шанс, что дроп сработал и всё равно ничего
   * не дал, — и его надо видеть, иначе шанс дропа читается как шанс лута.
   */
  perKill: number;
  /** true — это вес «не выпало ничего». */
  empty: boolean;
}

export const EMPTY_DROP_KIND = 'None';

/**
 * Разбор секции `drops` пресета в строки таблицы с шансами.
 *
 * Пустая таблица и отсутствие таблицы — разные вещи, и обе дают пустой
 * массив; отличать их надо по самому `config.drops`, а не по длине
 * результата.
 */
export function dropTableRows(drops: DropsConfig | undefined): DropTableRow[] {
  if (!drops || !Array.isArray(drops.entries)) return [];
  const total = drops.entries.reduce((sum, entry) => sum + Math.max(0, entry.weight), 0);
  const chance = Math.max(0, Math.min(100, drops.chancePercent)) / 100;
  return drops.entries.map((entry) => {
    const share = total > 0 ? Math.max(0, entry.weight) / total : 0;
    return {
      kind: entry.kind,
      amount: entry.amount,
      weight: entry.weight,
      share,
      perKill: chance * share,
      empty: entry.kind === EMPTY_DROP_KIND,
    };
  });
}

/**
 * Ожидаемое число предметов с одного трупа: шанс × доля непустых строк.
 * Именно оно, а не шанс дропа, отвечает на вопрос «сколько добавит дроп».
 */
export function expectedItemsPerKill(drops: DropsConfig | undefined): number {
  const rows = dropTableRows(drops);
  if (rows.length === 0) return 0;
  return rows.filter((row) => !row.empty).reduce((sum, row) => sum + row.perKill, 0);
}

// --- измеренная зависимость «шанс → сломанный дефицит» ----------------------

export interface DeficitPoint {
  chancePercent: number;
  heldShare: number;
  brokenShare: number;
}

/**
 * Измерено на 1000 прогонов базового пресета с таблицей «патроны ×3
 * вес 4, аптечка вес 1, пусто вес 3» — CONTRACT.md, §1, «Дроп с врагов».
 *
 * ЭТО ЗАМЕР, А НЕ МОДЕЛЬ: доля не выводится из шанса формулой, потому
 * что дефицит ломается только там, где запас патронов и без того лежал
 * у нуля, а таких уровней тем больше, чем плотнее враги. Считать её
 * «шанс × что-нибудь» было бы догадкой, выглядящей как измерение.
 *
 * Таблица привязана к ТОЙ САМОЙ таблице дропа: поменяется состав весов —
 * числа станут неверны, и их надо перемерить, а не пересчитать. Поэтому
 * рядом с ней в интерфейсе всегда стоит, на чём она снята.
 */
export const MEASURED_DEFICIT_CURVE: readonly DeficitPoint[] = [
  { chancePercent: 0, heldShare: 1.0, brokenShare: 0.0 },
  { chancePercent: 10, heldShare: 0.949, brokenShare: 0.051 },
  { chancePercent: 20, heldShare: 0.869, brokenShare: 0.131 },
  { chancePercent: 25, heldShare: 0.812, brokenShare: 0.188 },
  { chancePercent: 40, heldShare: 0.588, brokenShare: 0.412 },
];

export const MEASURED_CURVE_RUNS = 1000;
export const MEASURED_CURVE_TABLE = 'патроны ×3 вес 4, аптечка вес 1, пусто вес 3';

/**
 * Ближайшая измеренная точка к заданному шансу. Возвращает null, если
 * шанс лёг ровно между двумя замерами дальше чем на 5 процентных пунктов:
 * интерполировать замер — значит выдать выдумку за измерение.
 */
export function nearestMeasured(chancePercent: number): DeficitPoint | null {
  let best: DeficitPoint | null = null;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const point of MEASURED_DEFICIT_CURVE) {
    const gap = Math.abs(point.chancePercent - chancePercent);
    if (gap < bestGap) {
      bestGap = gap;
      best = point;
    }
  }
  return bestGap <= 5 ? best : null;
}
